/**
 * 聊天相关 API 服务模块
 * 基于 AI-Chat-API-Documentation.md 接口规范实现
 * API 基础路径：/api
 */

import request from '../utils/request';
import type { ApiResponse } from '@/types';

// ==================== 接口响应类型定义 ====================

/** 后端会话记录 */
export interface ChatSessionRecord {
  id: number;
  title: string;
  user_id?: number;
  created_at: string;
  updated_at: string;
}

/** 后端消息记录 */
export interface ChatMessageRecord {
  id: number;
  session_id: number;
  role: 'user' | 'assistant';
  content: string;
  thinking?: string | null;
  sort_order: number;
  created_at: string;
}

/** 会话列表响应 data */
export interface ChatSessionListData {
  list: ChatSessionRecord[];
  pageNum: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** 发送消息（非流式）响应 data */
export interface SendMessageData {
  session: ChatSessionRecord;
  userMessage: ChatMessageRecord;
  aiMessage: ChatMessageRecord | null;
  error?: string;
}

/** SSE 流式事件块 */
export interface SSEEventChunk {
  type: 'content' | 'thinking' | 'done' | 'final' | 'error';
  content?: string;
  thinking?: string;
  messageId?: number;
  error?: string;
}

// ==================== API 方法 ====================

/** 聊天相关 API，路径遵循 RESTful 规范 */
export const chatAPI = {
  // ========== 会话管理 (3.1) ==========

  /**
   * 3.1.1 新增会话
   * POST /api/sessions
   * @param title 会话标题（可选，默认"新对话"）
   * @param userId 用户 ID（可选，默认 0）
   */
  createSession: (
    title?: string,
    userId?: number
  ): Promise<ApiResponse<ChatSessionRecord>> =>
    request.post('/sessions', { title, user_id: userId }),

  /**
   * 3.1.2 删除会话
   * DELETE /api/sessions/:sessionId
   * @param sessionId 会话 ID
   */
  deleteSession: (sessionId: number): Promise<ApiResponse<null>> =>
    request.delete(`/sessions/${sessionId}`),

  /**
   * 3.1.3 查询会话列表
   * GET /api/sessions
   * @param params.userId 按用户 ID 筛选
   * @param params.page 页码，默认 1
   * @param params.pageSize 每页条数，默认 20，最大 100
   * @param params.sort 排序方式，默认 updated_at DESC
   */
  getSessionList: (
    params: {
      userId?: number;
      page?: number;
      pageSize?: number;
      sort?: string;
    } = {}
  ): Promise<ApiResponse<ChatSessionListData>> => {
    // 将驼峰参数转为下划线（后端使用 snake_case）
    const query: Record<string, unknown> = {};
    if (params.userId !== undefined) query.user_id = params.userId;
    if (params.page !== undefined) query.page = params.page;
    if (params.pageSize !== undefined) query.page_size = params.pageSize;
    if (params.sort !== undefined) query.sort = params.sort;
    return request.get('/sessions', { params: query });
  },

  /**
   * 3.1.4 查询会话详情
   * GET /api/sessions/:sessionId
   * @param sessionId 会话 ID
   */
  getSessionDetail: (
    sessionId: number
  ): Promise<ApiResponse<ChatSessionRecord>> =>
    request.get(`/sessions/${sessionId}`),

  // ========== 消息管理 (3.2) ==========

  /**
   * 3.2.1 查询消息列表
   * GET /api/sessions/:sessionId/messages
   * @param sessionId 会话 ID
   * @param params.page 页码，默认 1
   * @param params.pageSize 每页条数，默认 20
   * @param params.sort 排序方式，默认 sort_order ASC
   */
  getMessages: (
    sessionId: number,
    params: { page?: number; pageSize?: number; sort?: string } = {}
  ): Promise<ApiResponse<{ list: ChatMessageRecord[]; pageNum: number; pageSize: number; total: number; totalPages: number }>> => {
    const query: Record<string, unknown> = {};
    if (params.page !== undefined) query.page = params.page;
    if (params.pageSize !== undefined) query.page_size = params.pageSize;
    if (params.sort !== undefined) query.sort = params.sort;
    return request.get(`/sessions/${sessionId}/messages`, { params: query });
  },

  // ========== AI 聊天 (3.3) ==========

  /**
   * 3.3.1 发送消息（非流式）
   * POST /api/sessions/:sessionId/messages
   * @param sessionId 会话 ID（传 0 时自动创建新会话）
   * @param content 用户消息内容（Markdown 格式）
   * @param userId 用户 ID（可选）
   */
  sendMessage: (
    sessionId: number,
    content: string,
    userId?: number
  ): Promise<ApiResponse<SendMessageData>> =>
    request.post(`/sessions/${sessionId}/messages`, {
      content,
      user_id: userId,
    }),

  /**
   * 3.3.2 发送消息（SSE 流式输出）
   * POST /api/sessions/:sessionId/messages/stream
   * @param sessionId 会话 ID（传 0 时自动创建新会话）
   * @param content 用户消息内容
   * @param userId 用户 ID（可选）
   * @returns fetch Response 对象，可直接读取 body 流
   */
  sendMessageStream: (
    sessionId: number,
    content: string,
    userId?: number,
    signal?: AbortSignal
  ): Promise<Response> => {
    const baseURL =
      process.env.NODE_ENV === 'production'
        ? `${window.location.origin}/api`
        : 'http://localhost:3002/api';
    return fetch(`${baseURL}/sessions/${sessionId}/messages/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, user_id: userId }),
      signal,
    });
  },
};

/**
 * 上传图片到服务器
 * POST /api/upload/image
 * @param file 图片文件
 * @returns 上传成功后的图片 URL
 */
export const uploadImage = async (
  file: File
): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append('image', file);

  const res = await request.post<{ url: string }>('/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  if (!res.success || !res.data?.url) {
    throw new Error(res.message || '图片上传失败');
  }

  return { url: res.data.url };
};
